import { ALL_NOTES, MODE_NAMES_BY_SCALE, MODES, getNotesInMode as getNotesInModeFromMusicTheory, getChordNotes, getModeIntervals, resolveAllNotesIndex } from '../utils/musicTheory';
import { getGuitarTuning } from '../utils/guitarNeckUtils';

// Re-export to preserve existing imports across components. Read at usage via the
// function (the active tuning lives in the user mirror, hydrated after import).
export { getGuitarTuning };

// Reference intervals for comparing modes
export const IONIAN_INTERVALS = getModeIntervals('Ionian');
export const AEOLIAN_INTERVALS = getModeIntervals('Aeolian');

// Determine number of notes per string for the pattern based on scale size
export const getNotesPerString = (mode) => {
  const modeIntervals = getModeIntervals(mode);
  if (!modeIntervals) return 3; // Default to 3 if mode not found
  
  const scaleSize = modeIntervals.length;
  
  // 5-note scales (pentatonic, most Japanese scales) - use 2 notes per string
  if (scaleSize === 5) return 2;
  
  // 6-note scales (blues, whole tone, prometheus) - use 2 notes per string for cleaner patterns
  // This works better since 6/2=3 strings gives a nice complete pattern
  if (scaleSize === 6) return 2;
  
  // 8-note scales (diminished) - use 3 notes for more efficient patterns (8/3≈2.67 strings)
  if (scaleSize === 8) return 3;
  
  // 12-note scale (chromatic) - use 4 notes per string (using 3 would require 4 strings)
  if (scaleSize === 12) return 4;
  
  // Default for 7-note scales (standard diatonic scales) - 3 notes per string
  return 3;
};

// Legacy constant for backward compatibility
export const NOTES_PER_STRING = 3;

// Standard fret marker positions
export const SINGLE_DOT_FRETS = [3, 5, 7, 9, 15, 17, 19, 21];
export const DOUBLE_DOT_FRETS = [12, 24];

// Incrustations du manche (les points nacres d'une vraie guitare), placees
// entre deux cordes. Une frontiere k separe la ligne k-1 de la ligne k, en
// comptant les lignes du haut (corde aigue) vers le bas. Point simple entre
// les deux cordes du milieu, points doubles au quart et aux trois quarts.
export const getFretInlays = (startFret, numFretsToShow, numStrings) => {
  if (numStrings < 2) return [];
  const quarter = Math.round(numStrings / 4);
  const inlays = [];
  for (let fretIndex = 0; fretIndex <= numFretsToShow; fretIndex++) {
    const fret = startFret + fretIndex;
    if (SINGLE_DOT_FRETS.includes(fret)) {
      inlays.push({ fret, fretIndex, boundary: Math.floor(numStrings / 2) });
    } else if (DOUBLE_DOT_FRETS.includes(fret)) {
      inlays.push({ fret, fretIndex, boundary: quarter });
      inlays.push({ fret, fretIndex, boundary: numStrings - quarter });
    }
  }
  return inlays;
};

// Get all notes in a mode starting from a root note
// Use the proper spelling from musicTheory.js
export const getNotesInMode = (root, mode) => {
  return getNotesInModeFromMusicTheory(root, mode);
};

// Check if a note is a "mode color note" - uses the colorDegrees defined for each mode
export const isModeColorNote = (note, activeScale) => {
  if (!activeScale || !activeScale.notes) return false;
  
  // Get the mode definition from MODES
  const mode = MODES[activeScale.modeName];
  if (!mode || !mode.colorDegrees) return false;
  
  // If no color degrees defined for this mode, return false
  if (mode.colorDegrees.length === 0) return false;
  
  // Find which scale degree this note is (1-based index)
  const noteIndex = activeScale.notes.indexOf(note);
  if (noteIndex === -1) return false;
  
  // Convert to 1-based scale degree (1, 2, 3, etc.)
  const scaleDegree = noteIndex + 1;
  
  // Check if this scale degree is in the colorDegrees array
  return mode.colorDegrees.includes(scaleDegree);
};

// Get the reference note (unaltered note from Ionian/Aeolian) for a mode color note
export const getReferenceNoteForModeColorNote = (note, activeScale) => {
  if (!activeScale) return null;
  
  const noteIndex = ALL_NOTES.indexOf(note);
  const rootIndex = ALL_NOTES.indexOf(activeScale.scaleRoot);
  
  // Get the current mode's intervals
  const currentModeIntervals = getModeIntervals(activeScale.modeName);
  if (!currentModeIntervals) return null;
  
  // Determine if the current mode is major-based or minor-based
  const isMajorBased = ['Ionian', 'Lydian', 'Mixolydian'].includes(activeScale.modeName) || 
                     activeScale.modeName.includes('Major') || 
                     activeScale.modeName.includes('Lydian') || 
                     activeScale.modeName.includes('Mixolydian');
  
  const isMinorBased = ['Aeolian', 'Dorian', 'Phrygian', 'Locrian'].includes(activeScale.modeName) || 
                     activeScale.modeName.includes('Minor') || 
                     activeScale.modeName.includes('Dorian') || 
                     activeScale.modeName.includes('Phrygian') || 
                     activeScale.modeName.includes('Locrian');
  
  // Get the reference intervals (Ionian for major, Aeolian for minor)
  const referenceIntervals = isMajorBased ? IONIAN_INTERVALS : 
                          isMinorBased ? AEOLIAN_INTERVALS : 
                          IONIAN_INTERVALS; // Default to Ionian if unsure
  
  // Find the interval of this note from the root
  const noteInterval = (noteIndex - rootIndex + 12) % 12;
  
  // Get the position of this interval in the current mode
  const intervalPosition = currentModeIntervals.indexOf(noteInterval);
  
  // Get the corresponding interval in the reference mode
  const referenceInterval = referenceIntervals[intervalPosition];
  
  // Calculate the reference note
  const referenceNoteIndex = (rootIndex + referenceInterval) % 12;
  return ALL_NOTES[referenceNoteIndex];
};

// Generate notes for a guitar string starting from a specific tuning
export const generateStringNotes = (openNote, numFrets) => {
  const allNotes = ALL_NOTES;
  const openNoteIndex = allNotes.indexOf(openNote);
  const notes = [];
  
  for (let i = 0; i <= numFrets; i++) {
    const noteIndex = (openNoteIndex + i) % 12;
    notes.push(allNotes[noteIndex]);
  }
  
  return notes;
};

// Get the note at a specific fret on a specific string
export const getNoteAtFret = (openNote, fret) => {
  const allNotes = ALL_NOTES;
  const openNoteIndex = allNotes.indexOf(openNote);
  const noteIndex = (openNoteIndex + fret) % 12;
  return allNotes[noteIndex];
};

// Find the fret position of a specific note on a string
export const findFretForNote = (stringIndex, note, startFret, numFretsToShow) => {
  const openNote = getGuitarTuning()[stringIndex];
  const openNoteIndex = ALL_NOTES.indexOf(openNote);
  
  // Find the closest fret position for this note
  // We'll look within the visible range first
  for (let fret = startFret; fret <= startFret + numFretsToShow; fret++) {
    const fretNoteIndex = (openNoteIndex + fret) % 12;
    if (ALL_NOTES[fretNoteIndex] === note) {
      return fret;
    }
  }
  
  // If not found in visible range, look in the entire neck
  for (let fret = 0; fret < 24; fret++) {
    const fretNoteIndex = (openNoteIndex + fret) % 12;
    if (ALL_NOTES[fretNoteIndex] === note) {
      return fret;
    }
  }
  
  return -1; // Note not found on this string
};

// Scale and mode related functions
export const getScaleForActiveChord = (rootNote, currentMode, activeChordNote) => {
  // First, ensure we can get notes for the current mode
  let notesInMode;
  try {
    notesInMode = getNotesInMode(rootNote, currentMode);
  } catch (error) {
    console.warn(`[GuitarNeckVisualizer] Error getting notes for mode ${currentMode}, falling back to Ionian`, error);
    notesInMode = getNotesInMode(rootNote, 'Ionian');
    currentMode = 'Ionian'; // Update the current mode to match our fallback
  }
  
  // Locate by pitch class: the scale may be spelled with flats while
  // activeChordNote/rootNote carry the sharp key spelling.
  const activeChordPitch = resolveAllNotesIndex(activeChordNote);
  const activeChordIndex = notesInMode.findIndex(n => resolveAllNotesIndex(n) === activeChordPitch);

  if (!activeChordNote || activeChordIndex === -1 || activeChordPitch === resolveAllNotesIndex(rootNote)) {
    // If no active chord or active chord is the root, use the original mode
    return { 
      notes: notesInMode, 
      rootIndex: 0,
      modeName: currentMode,
      scaleRoot: rootNote
    };
  }
  
  // Extract the scale type from the currentMode
  let scaleType = 'Major'; // Default
  
  // Determine the scale type based on the current mode
  for (const type of Object.keys(MODE_NAMES_BY_SCALE)) {
    if (MODE_NAMES_BY_SCALE[type].includes(currentMode)) {
      scaleType = type;
      break;
    }
  }
  
  // For non-Western scales with only one mode or 5/6-note scales, special handling
  const currentModeIntervals = getModeIntervals(currentMode);
  if (MODE_NAMES_BY_SCALE[scaleType].length === 1 || 
      (currentModeIntervals && (currentModeIntervals.length === 5 || currentModeIntervals.length === 6))) {
    // Pentatonic/blues/non-Western: keep a FIXED scale rooted on the progression root
    // and compute chord tones (limited to notes within this fixed scale)
    try {
      // Determine a diatonic parent to infer chord quality when possible
      const parentMode = (
        (currentMode && currentMode.toLowerCase().includes('minor pentatonic')) ? 'Aeolian' :
        (currentMode && currentMode.toLowerCase().includes('major pentatonic')) ? 'Ionian' :
        null
      );
      let inferredQuality = 'maj';
      if (parentMode) {
        const parentNotes = getNotesInModeFromMusicTheory(rootNote, parentMode);
        const degreeIndex = parentNotes.findIndex(n => resolveAllNotesIndex(n) === activeChordPitch);
        if (degreeIndex !== -1 && MODES[parentMode] && MODES[parentMode].chordQualities) {
          inferredQuality = MODES[parentMode].chordQualities[degreeIndex] || inferredQuality;
        } else {
          inferredQuality = parentMode === 'Aeolian' ? 'min' : 'maj';
        }
      } else {
        const lc = (currentMode || '').toLowerCase();
        inferredQuality = (lc.includes('minor') || lc.includes('blues')) ? 'min' : 'maj';
      }
      // Get chord notes and intersect with the fixed scale notes
      const chordNotes = getChordNotes(activeChordNote, inferredQuality);
      const scalePitches = new Set(notesInMode.map(resolveAllNotesIndex));
      const chordTones = chordNotes.filter(n => scalePitches.has(resolveAllNotesIndex(n)));
      return {
        notes: notesInMode,
        rootIndex: 0,
        modeName: currentMode,
        scaleRoot: rootNote,
        chordTones
      };
    } catch (error) {
      console.warn(`[GuitarNeckVisualizer] Error computing chord tones for ${currentMode} with chord ${activeChordNote}`, error);
      return {
        notes: notesInMode,
        rootIndex: 0,
        modeName: currentMode,
        scaleRoot: rootNote,
        chordTones: []
      };
    }
  }
  
  // Standard handling for Western scales with multiple modes
  // Calculate what mode we should show based on the active chord and scale type
  const modeNames = MODE_NAMES_BY_SCALE[scaleType];
  
  // Rotate the mode list so that the currentMode is treated as degree I
  const currentModeIndex = modeNames.indexOf(currentMode);
  const rotatedIndex = (currentModeIndex + activeChordIndex) % modeNames.length;
  const relativeModeName = modeNames[rotatedIndex] || modeNames[0];

  

  // Safety check for relativeModeName
  if (!getModeIntervals(relativeModeName)) {
    console.warn(`Mode ${relativeModeName} not found in MODES, falling back to ${currentMode}`);
    relativeModeName = getModeIntervals(currentMode) ? currentMode : 'Ionian';
  }
  
  // Get the notes of the relative mode starting from the active chord note
  let relativeScaleNotes;
  try {
    relativeScaleNotes = getNotesInMode(activeChordNote, relativeModeName);
  } catch (error) {
    console.warn(`[GuitarNeckVisualizer] Error getting notes for ${relativeModeName}, falling back to Ionian`);
    relativeScaleNotes = getNotesInMode(activeChordNote, 'Ionian');
    relativeModeName = 'Ionian';
  }
  
  return {
    notes: relativeScaleNotes,
    rootIndex: 0,
    modeName: relativeModeName,
    scaleRoot: activeChordNote
  };
};

// Always show all 24 frets for a swipeable fretboard
export const calculateOptimalFretboardLayout = (activeScale, availableWidth, isLandscape, isLargeScreen, isMaximized, isMobile) => {
  // Calculate the root fret position (still needed for highlighting)
  const rootFret = findRootFret(activeScale);
  
  // Always start from fret 1 and show exactly 24 frets (not 25)
  const startFret = 1;
  const numFretsToShow = 23; // This will show frets 1-24 (24 frets total)
  
  return {
    startFret,
    numFretsToShow,
    rootFret
  };
};

// Find the fret position of the root note on the low E string
const findRootFret = (activeScale) => {
  // Check if activeScale is null
  if (!activeScale) {
    return 1; // Default to fret 1 if activeScale is null
  }
  
  // Find the position of the root note on the low E string
  const displayRoot = activeScale.scaleRoot;
  const lowEString = generateStringNotes('E', 24);
  const rootIndex = lowEString.indexOf(displayRoot);
  
  return rootIndex >= 0 ? rootIndex : 1;
};

// Calculate the number of frets to show based on available space and device
const calculateNumFretsToShow = (availableWidth, isLandscape, isLargeScreen, isMaximized) => {
  // Constants for calculations
  const MIN_FRET_WIDTH = 40; // Minimum width of a single fret in pixels
  const TARGET_FRET_WIDTH = 55; // Ideal width of a single fret in pixels
  
  // Calculate how many frets could fit based on available width and minimum fret width
  const maxPossibleFrets = Math.floor(availableWidth / MIN_FRET_WIDTH);
  
  // Calculate ideal number of frets based on target width
  const idealFrets = Math.floor(availableWidth / TARGET_FRET_WIDTH);
  
  // Base calculation on screen size and orientation
  if (isMaximized) {
    // In maximized view, show more frets for landscape
    if (isLandscape) {
      return Math.min(Math.max(idealFrets, 14), 24); // Between 14 and 24 frets
    } else {
      return Math.min(Math.max(idealFrets, 8), 14); // Between 8 and 14 frets
    }
  }
  
  // For normal view
  if (isLargeScreen) {
    return Math.min(Math.max(idealFrets, 12), 20); // Between 12 and 20 frets
  } else if (isLandscape) {
    return Math.min(Math.max(idealFrets, 10), 18); // Between 10 and 18 frets
  } else {
    return Math.min(Math.max(idealFrets, 8), 12); // Between 8 and 12 frets
  }
};

// Calculate an adaptive start fret based on the root fret and available space
const calculateAdaptiveStartFret = (rootFret, numFretsToShow) => {
  // If the root is at a low fret (0-3), start from fret 1
  if (rootFret <= 3) {
    return 1;
  }
  
  // If we have enough space to show frets before the root
  // Try to start from fret 1 if possible
  if (rootFret <= numFretsToShow - 2) {
    return 1;
  }
  
  // Try to position the root note around 1/3 of the way through the visible range
  // This gives context before the root note while showing enough frets after it
  const targetPosition = Math.floor(numFretsToShow / 3);
  let startFret = Math.max(1, rootFret - targetPosition);
  
  // Ensure we're not too close to the end of the neck
  const distanceToEnd = 24 - (startFret + numFretsToShow - 1);
  if (distanceToEnd < 0) {
    startFret = Math.max(1, startFret + distanceToEnd);
  }
  
  // Final sanity check
  return Math.max(1, startFret);
};

// Legacy function for backward compatibility
export const calculateStartFret = (activeScale) => {
  // Check if activeScale is null
  if (!activeScale) {
    return 1; // Default to fret 1 if activeScale is null
  }
  
  // Find the position of the active chord note on the low E string
  const displayRoot = activeScale.scaleRoot;
  const lowEString = generateStringNotes('E', 24);
  const rootIndex = lowEString.indexOf(displayRoot);
  
  if (rootIndex >= 0) {
    // Calculate starting position (2 frets before root)
    let startFret = rootIndex - 2;
    
    // If starting position would be fret 0 or negative, use fret 1 instead
    if (startFret < 1) {
      startFret = 1;
    }
    
    return startFret;
  }
  
  // Default to fret 1 if root note not found
  return 1;
};
